import type { ToothData } from '../types';

type OdontogramProps = {
  teethData: Record<number, ToothData>;
  selectedTeeth: number[];
  onToothClick: (toothNumber: number) => void;
};

type ToothProps = {
  number: number;
  isSelected: boolean;
  isConfigured: boolean;
  onClick: () => void;
};

const UPPER_RIGHT_PATIENT = [11, 12, 13, 14, 15, 16, 17, 18].reverse();
const UPPER_LEFT_PATIENT = [21, 22, 23, 24, 25, 26, 27, 28];
const LOWER_RIGHT_PATIENT = [41, 42, 43, 44, 45, 46, 47, 48].reverse();
const LOWER_LEFT_PATIENT = [31, 32, 33, 34, 35, 36, 37, 38];

const TOOTH_TYPE: Record<string, string> = {
  '1': 'Kesici',
  '2': 'Kesici',
  '3': 'Köpek',
  '4': 'Küçük Azı',
  '5': 'Küçük Azı',
  '6': 'Büyük Azı',
  '7': 'Büyük Azı',
  '8': 'Yirmi Yaş',
};

function getToothType(num: number): string {
  const lastDigit = String(num).slice(-1);
  return TOOTH_TYPE[lastDigit] || '';
}

function Tooth({ number, isSelected, isConfigured, onClick }: ToothProps) {
  const isMolar = [6, 7, 8].includes(number % 10);
  const isWisdom = number % 10 === 8;

  return (
    <button
      type="button"
      onClick={onClick}
      title={`Diş ${number} — ${getToothType(number)}`}
      className={`
        flex flex-col items-center justify-center rounded-md transition-all cursor-pointer relative
        ${isMolar ? 'w-5 h-6 sm:w-7 sm:h-9 md:w-10 md:h-12' : isWisdom ? 'w-5 h-6 sm:w-6 sm:h-8 md:w-9 md:h-11' : 'w-5 h-6 sm:w-6 sm:h-8 md:w-9 md:h-10'}
        ${isSelected
          ? 'bg-teal-500 text-white ring-2 ring-teal-400 ring-offset-1 scale-110'
          : isConfigured
          ? 'bg-teal-100 text-teal-700 ring-1 ring-teal-300 hover:bg-teal-200'
          : 'bg-white text-gray-500 border border-gray-200 hover:border-teal-300 hover:text-teal-600 hover:bg-teal-50'
        }
      `}
    >
      <span className="text-[9px] sm:text-[10px] md:text-xs font-bold leading-none">{number}</span>
      {isConfigured && !isSelected && (
        <span className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 bg-teal-500 rounded-full flex items-center justify-center">
          <i className="ri-check-line text-white" style={{ fontSize: '6px' }}></i>
        </span>
      )}
    </button>
  );
}

export default function Odontogram({ teethData, selectedTeeth, onToothClick }: OdontogramProps) {
  const isConfigured = (n: number) => !!teethData[n];
  const isSelected = (n: number) => selectedTeeth.includes(n);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 px-2 py-4 sm:p-4 md:p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-sm font-bold text-gray-800">Odontogram</h3>
          <p className="text-xs text-gray-400 mt-0.5">FDI Numaralandırma — Tedavi yapılacak dişe tıklayın</p>
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-white border border-gray-300 inline-block"></span>
            Seçilmedi
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-teal-100 border border-teal-300 inline-block"></span>
            Girildi
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-teal-500 inline-block"></span>
            Aktif
          </span>
        </div>
      </div>

      {/* JAW CONTAINER */}
      <div className="flex flex-col items-center gap-1 select-none">

        {/* UPPER JAW LABEL */}
        <div className="w-full flex justify-between px-4 mb-1">
          <span className="text-xs text-gray-400 font-medium">SAĞ ÜST</span>
          <span className="text-xs text-gray-400 font-medium">SOL ÜST</span>
        </div>

        {/* UPPER TEETH */}
        <div className="flex items-end gap-px sm:gap-0.5 bg-gray-50 rounded-xl px-1 sm:px-2 md:px-4 py-2 sm:py-3 w-full justify-center">
          {/* Q1: Upper Right — 18→11 (displayed left to right) */}
          <div className="flex items-end gap-px sm:gap-0.5">
            {UPPER_RIGHT_PATIENT.map((n) => (
              <Tooth
                key={n}
                number={n}
                isSelected={isSelected(n)}
                isConfigured={isConfigured(n)}
                onClick={() => onToothClick(n)}
              />
            ))}
          </div>

          {/* Midline */}
          <div className="w-px h-8 sm:h-10 md:h-12 bg-gray-300 mx-0.5 sm:mx-1 flex-shrink-0" />

          {/* Q2: Upper Left — 21→28 (displayed left to right) */}
          <div className="flex items-end gap-px sm:gap-0.5">
            {UPPER_LEFT_PATIENT.map((n) => (
              <Tooth
                key={n}
                number={n}
                isSelected={isSelected(n)}
                isConfigured={isConfigured(n)}
                onClick={() => onToothClick(n)}
              />
            ))}
          </div>
        </div>

        {/* ARCH SPACER */}
        <div className="w-full flex items-center gap-2 px-4 py-1">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-xs text-gray-400 font-semibold px-2">Orta Hat</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>

        {/* LOWER TEETH */}
        <div className="flex items-start gap-px sm:gap-0.5 bg-gray-50 rounded-xl px-1 sm:px-2 md:px-4 py-2 sm:py-3 w-full justify-center">
          {/* Q4: Lower Right — 48→41 (displayed left to right) */}
          <div className="flex items-start gap-px sm:gap-0.5">
            {LOWER_RIGHT_PATIENT.map((n) => (
              <Tooth
                key={n}
                number={n}
                isSelected={isSelected(n)}
                isConfigured={isConfigured(n)}
                onClick={() => onToothClick(n)}
              />
            ))}
          </div>

          {/* Midline */}
          <div className="w-px h-8 sm:h-10 md:h-12 bg-gray-300 mx-0.5 sm:mx-1 flex-shrink-0" />

          {/* Q3: Lower Left — 31→38 (displayed left to right) */}
          <div className="flex items-start gap-px sm:gap-0.5">
            {LOWER_LEFT_PATIENT.map((n) => (
              <Tooth
                key={n}
                number={n}
                isSelected={isSelected(n)}
                isConfigured={isConfigured(n)}
                onClick={() => onToothClick(n)}
              />
            ))}
          </div>
        </div>

        {/* LOWER JAW LABEL */}
        <div className="w-full flex justify-between px-4 mt-1">
          <span className="text-xs text-gray-400 font-medium">SAĞ ALT</span>
          <span className="text-xs text-gray-400 font-medium">SOL ALT</span>
        </div>
      </div>
    </div>
  );
}
